import { useState, useEffect, useMemo } from 'react'
import { Button, Fa, Text } from '@eltoro-ui/components'
import classNames from 'classnames'
import { useValidatedState } from '@eltoro-ui/hooks'
import { Creativeservicev1Creative } from 'next-gen-sdk'
import { styleTailwind, simplifyEnum, urlHasUTM } from 'Helpers'
import { TextInputWithErrorText } from 'Components/TextInputWithErrorText'
import { ModifiedOrderLineType } from '../types'
import { ClickThroughWithUTM } from 'Components/AdSetForms/components/ClickThroughWithUTM'
import { useGetFullOLCreatives } from 'Hooks'
import { CreativeSubTableRow } from './CreativeSubTableRow'

const HeaderText = styleTailwind(Text, 'EditOLTable__header-text')
const EditOLRowItem = styleTailwind(Text, 'EditOLRowItem__item')
const TableGrid = styleTailwind(
  'div',
  'EditOLTable__TableGrid laptopsmall:flex laptopsmall:flex-wrap laptopsmall:justify-between laptopsmall:gap-y-4',
)
const CreativeTableGrid = styleTailwind(
  'div',
  'CreativeEditOLTable__CreativeTableGrid laptopsmall:flex laptopsmall:flex-wrap laptopsmall:justify-between laptopsmall:gap-y-4',
)
const TabletHeader = styleTailwind(
  HeaderText,
  'EditOLTable__TabletHeader block',
)
const UTMEditorButton = styleTailwind(Button, 'UTMEditorButton text-primary')
const UTMEditorTooltip = styleTailwind('span', 'UTMEditorTooltip w-auto')
const StackBtn = styleTailwind('div', 'StackedButtonIcon group')

export type CreativeEditOLTableOrderLineRowProps = {
  orderLine: ModifiedOrderLineType
  isTablet: boolean
  handleUpdateClickThru: (
    clickThroughUrl: string,
    modifiedOL: ModifiedOrderLineType,
  ) => void
  handleRemoveCreative: (
    orderLine: ModifiedOrderLineType,
    creative?: Creativeservicev1Creative,
  ) => void
  handleUpdateBrandURL?: (
    brandUrls: string[],
    modifiedOL: ModifiedOrderLineType,
  ) => void
  handleRemoveBrandURL?: (orderLine: ModifiedOrderLineType) => void
}

export const CreativeEditOLTableOrderLineRow = ({
  orderLine,
  handleUpdateClickThru,
  handleRemoveCreative,
  handleUpdateBrandURL,
  handleRemoveBrandURL,
  isTablet,
}: CreativeEditOLTableOrderLineRowProps) => {
  const [editingClickThru, setEditingClickThru] = useState(false)
  const [editingBrandUrl, setEditingBrandUrl] = useState(false)
  const [clickThru, setClickThru, isValidClickThru] = useValidatedState(
    orderLine.clickThroughUrl || 'https://',
    'url',
  )
  const [brandUrl, setBrandUrl, isValidBrandURL] = useValidatedState(
    'https://',
    'url',
  )

  const orderLineType = simplifyEnum(2, orderLine.adType || '', true)
  const creativeCount = (orderLine.creatives && orderLine.creatives.length) || 0

  // Get brand URLs from creative metadata
  const getCreativeBrandUrls = (creatives: Creativeservicev1Creative[]) => {
    const brandUrls: string[] = []
    creatives.forEach((creative) => {
      if (creative.nativeMetadata?.alternativeMobileLandingPageUrl) {
        brandUrls.push(creative.nativeMetadata.alternativeMobileLandingPageUrl)
      }
    })
    return [...new Set(brandUrls)] // Remove duplicates
  }

  useEffect(() => {
    // reset the text input value if parent changes
    setClickThru(orderLine.clickThroughUrl || '')
  }, [orderLine.clickThroughUrl, setClickThru])

  const [creatives, loading] = useGetFullOLCreatives(
    orderLine.creatives,
    orderLine.orgId,
  )

  // Get brand URLs from creatives when they load
  const creativeBrandUrls = useMemo(() => {
    return creatives ? getCreativeBrandUrls(creatives) : []
  }, [creatives])

  // Get effective brand URLs - prioritize modified order line's brandUrls over creative metadata
  const effectiveBrandUrls = useMemo(() => {
    // Check if this order line has been modified to remove brand URLs
    if (orderLine.brandUrlEdited && orderLine.brandUrls === undefined) {
      return [] // Return empty array when brand URLs have been explicitly removed
    }
    // Check if this order line has been modified and has brandUrls
    if (orderLine.brandUrls && orderLine.brandUrls.length > 0) {
      return orderLine.brandUrls
    }
    // Fall back to creative metadata only if not explicitly edited
    if (!orderLine.brandUrlEdited) {
      return creativeBrandUrls
    }
    // If edited but no brandUrls set, return empty array
    return []
  }, [orderLine.brandUrls, orderLine.brandUrlEdited, creativeBrandUrls])

  // Initialize brandUrl with first effective brand URL or default
  useEffect(() => {
    if (effectiveBrandUrls.length > 0 && brandUrl === 'https://') {
      setBrandUrl(effectiveBrandUrls[0])
    }
  }, [effectiveBrandUrls, brandUrl, setBrandUrl])

  return (
    <>
      <TableGrid className="EditOLRowItem CreativeEditOLTableOrderLineRow !bg-tint-khaki-50">
        <EditOLRowItem className="EditOLRowItem__name break-words">
          <TabletHeader>Order Line Name</TabletHeader>
          <Text className="OrderLineName break-all">
            {orderLine.name || 'No name'}
          </Text>
        </EditOLRowItem>
        <EditOLRowItem className="EditOLRowItem__ol-creative-info">
          <TabletHeader>Type & Count</TabletHeader>

          <Text>
            {orderLineType}; {creativeCount}
          </Text>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__click-through break-words">
          <TabletHeader>Click-through URL</TabletHeader>
          <div className="ClickThruWrap flex w-full items-center justify-between gap-2">
            {editingClickThru ? (
              <>
                <ClickThroughWithUTM
                  OLName={''}
                  clickThru={clickThru}
                  setClickThru={setClickThru}
                  isValidClickThru={isValidClickThru}
                  preview
                />
                <UTMEditorTooltip
                  className="mt-2"
                  data-tooltip="top"
                  aria-label="Save Click-through"
                >
                  <Fa
                    icon="save"
                    size={1}
                    className={classNames('Button', {
                      'border-grey-200 text-grey-200 pointer-events-none': !isValidClickThru,
                    })}
                    onClick={() => {
                      handleUpdateClickThru(clickThru, orderLine)
                      setEditingClickThru(!editingClickThru)
                    }}
                  />
                </UTMEditorTooltip>
              </>
            ) : (
              <div className="flex-1">
                <span
                  className={classNames(
                    'flex max-w-[40ch] flex-1 gap-2 break-all py-1 text-xs',
                    {
                      'text-warning font-bold': !orderLine.clickThroughUrl,
                    },
                  )}
                >
                  {orderLine?.clickThroughUrl ? (
                    <a
                      href={orderLine.clickThroughUrl.split('?')[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="OrderLine__clickThroughUrl flex items-center gap-2 py-2"
                    >
                      {orderLine.clickThroughUrl?.split('?')[0] ||
                        'No click-through for creative'}
                      <Fa
                        icon="arrow-up-right-from-square"
                        size={1}
                        className="text-primary inline-block pl-2 !text-xs"
                      />
                    </a>
                  ) : (
                    'No click-through URL'
                  )}
                  {urlHasUTM(clickThru) && (
                    <UTMEditorTooltip
                      className="text-primary ml-4 flex items-center justify-center font-semibold hover:cursor-pointer"
                      data-tooltip="left"
                      aria-label={`${clickThru.split('?')[1]}`}
                    >
                      <UTMEditorButton
                        className="!gap-0 !pl-1 !pr-3"
                        kind="text"
                        iconLeft={<Fa icon="check" size={1} />}
                        onClick={() => setEditingClickThru(!editingClickThru)}
                      >
                        UTM
                      </UTMEditorButton>
                    </UTMEditorTooltip>
                  )}
                </span>
              </div>
            )}
            <UTMEditorTooltip
              className="mt-2"
              data-tooltip="left"
              aria-label={
                editingClickThru ? 'Cancel Edit' : 'Edit Click-through URL'
              }
            >
              <UTMEditorButton
                iconOnly={
                  <Fa icon={editingClickThru ? 'xmark' : 'pencil'} size={1} />
                }
                onClick={() => setEditingClickThru(!editingClickThru)}
              />
            </UTMEditorTooltip>
          </div>
        </EditOLRowItem>

        <EditOLRowItem className="EditOLRowItem__brand-url break-words">
          <TabletHeader>Brand URL</TabletHeader>
          <div className="BrandURLWrap flex w-full items-center justify-between gap-2">
            {(() => {
              if (loading)
                return (
                  <span className="BrandURLLoader animate-pulse">
                    Loading Brand URLs
                  </span>
                )
              if (editingBrandUrl)
                return (
                  <>
                    <div className="flex flex-1 flex-col gap-2">
                      <TextInputWithErrorText
                        value={brandUrl}
                        onChange={(e) => setBrandUrl(e.target.value)}
                        isValid={isValidBrandURL && brandUrl.length <= 1000}
                        baseValues={['http://', 'https://']}
                        maxLength={1000}
                        popoverContent={(() => {
                          if (
                            !(
                              brandUrl.startsWith('http://') ||
                              brandUrl.startsWith('https://')
                            )
                          )
                            return (
                              <div>URL requires &quot;http(s)://&quot;</div>
                            )
                          return (
                            <div>
                              Please enter a valid url in the format:
                              &quot;https://www.websitename.com&quot;
                            </div>
                          )
                        })()}
                      />
                    </div>
                    <div className="flex gap-1">
                      <UTMEditorTooltip
                        className="self-center"
                        data-tooltip="top"
                        aria-label="Save Brand URL"
                      >
                        <UTMEditorButton
                          className={classNames({
                            'border-grey-200 text-grey-200 pointer-events-none': !isValidBrandURL,
                          })}
                          iconOnly={<Fa icon="save" size={1} />}
                          onClick={() => {
                            if (handleUpdateBrandURL)
                              handleUpdateBrandURL([brandUrl], orderLine)
                            setEditingBrandUrl(!editingBrandUrl)
                          }}
                        />
                      </UTMEditorTooltip>
                      <UTMEditorTooltip
                        className="self-center"
                        data-tooltip="top"
                        aria-label="Remove Brand URL"
                      >
                        <UTMEditorButton
                          iconOnly={<Fa icon="trash-xmark" size={1} />}
                          onClick={() => {
                            if (handleRemoveBrandURL)
                              handleRemoveBrandURL(orderLine)
                            setEditingBrandUrl(!editingBrandUrl)
                          }}
                        />
                      </UTMEditorTooltip>
                    </div>
                  </>
                )

              return (
                <div>
                  {effectiveBrandUrls.length === 0 && (
                    <Button
                      className={classNames(
                        'hover:!text-base-info !bg-transparent px-0',
                        {
                          'opacity-50 cursor-not-allowed': creativeCount === 0,
                        },
                      )}
                      kind="text"
                      disabled={creativeCount === 0}
                      onClick={() => {
                        if (creativeCount > 0) {
                          setEditingBrandUrl(!editingBrandUrl)
                        }
                      }}
                    >
                      <span className="text-m normal-case">
                        {creativeCount === 0
                          ? 'No creatives - Brand URL disabled'
                          : 'No brand URL is set'}
                      </span>
                    </Button>
                  )}
                  {effectiveBrandUrls.length > 0 && (
                    <div className="OrderLine__brandUrls flex flex-col items-start gap-1 break-all text-left leading-none">
                      {effectiveBrandUrls.map((currentBrand, i) => {
                        return (
                          <div key={`${currentBrand}_${i}`}>
                            {currentBrand.length > 8 ? (
                              <a
                                className="OrderLine__brandUrls--link text-m flex items-center gap-2 py-1"
                                href={currentBrand}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <span className="text-xs">{currentBrand}</span>
                                <Fa
                                  icon="arrow-up-right-from-square"
                                  size={1}
                                  className="text-primary !text-xs"
                                />
                              </a>
                            ) : (
                              <Button
                                className={classNames(
                                  'hover:!text-base-info !bg-transparent px-0',
                                  {
                                    'opacity-50 cursor-not-allowed':
                                      creativeCount === 0,
                                  },
                                )}
                                kind="text"
                                disabled={creativeCount === 0}
                                onClick={() => {
                                  if (creativeCount > 0) {
                                    setEditingBrandUrl(!editingBrandUrl)
                                  }
                                }}
                              >
                                <span className="text-m px-0 normal-case">
                                  No brand URL
                                </span>
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}
            <UTMEditorTooltip
              className="self-center"
              data-tooltip="left"
              aria-label={
                creativeCount === 0
                  ? 'Brand URL editing disabled - no creatives'
                  : editingBrandUrl
                  ? 'Cancel Edit'
                  : 'Edit Brand URL'
              }
            >
              <UTMEditorButton
                className={classNames({
                  'opacity-50 cursor-not-allowed': creativeCount === 0,
                })}
                disabled={creativeCount === 0}
                iconOnly={
                  <Fa icon={editingBrandUrl ? 'xmark' : 'pencil'} size={1} />
                }
                onClick={() => {
                  if (creativeCount > 0) {
                    setEditingBrandUrl(!editingBrandUrl)
                  }
                }}
              />
            </UTMEditorTooltip>
          </div>
        </EditOLRowItem>
        <EditOLRowItem className="EditOLRowItem__ol-creatives">
          <div className="StackedButtonIcon__wrap flex gap-1 self-center">
            <StackBtn
              className="group-hover:text-primary"
              data-tooltip="top"
              aria-label="Remove All Creatives from Order Line"
            >
              <Button
                iconCenter={
                  <Fa
                    icon="trash-xmark"
                    size={1}
                    className="StackedButtonIcon__icon"
                  />
                }
                onClick={() => handleRemoveCreative(orderLine)}
              >
                <span className="IconLabel text-s">Remove All</span>
              </Button>
            </StackBtn>
          </div>
        </EditOLRowItem>
      </TableGrid>
      {/* Creative Sub-table Rows */}
      {creatives && creatives.length > 0 && (
        <div className="CreativeSubTable">
          {isTablet === false && (
            <CreativeTableGrid className="EditOLHeader__header bg-base sticky top-0 z-10 shadow-md">
              <HeaderText className="truncate">Creative</HeaderText>
              <HeaderText className="truncate">Media Type</HeaderText>
              <HeaderText className="truncate">File Type</HeaderText>
              <HeaderText className="truncate">Dimensions</HeaderText>
              <HeaderText className="truncate">File Size</HeaderText>
              <HeaderText className="truncate">Status</HeaderText>
              <HeaderText className="truncate">Actions</HeaderText>
            </CreativeTableGrid>
          )}
          {creatives.map((creative, index) => (
            <CreativeSubTableRow
              key={`CreativeSubTableRow_${creative.id}_${index}`}
              creative={creative}
              orderLine={orderLine}
              handleRemoveCreative={handleRemoveCreative}
              isTablet={isTablet}
            />
          ))}
        </div>
      )}
    </>
  )
}
